import React from 'react';
import { Box, CircularProgress, Grid, Rating, Tooltip, Typography, useTheme } from '@mui/material';
import { Form, FormikHelpers, useFormikContext } from 'formik';
import * as Yup from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { parseIntOrNull } from 'lib/helpers';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiForm } from 'lib/Form';
import { getMinutesToExpiryComponents, getTimeUnitsToMinutes, TimeMeasure } from 'lib/timeHelpers';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { StyledButtonWrapper, StyledLoadingButton } from './ReviewsLocationSettings';

interface ReviewsGlobalSettingsFormData {
    reviewsEnabledByMerchant: boolean;
    minimumRating: string;
    cooldownAfterDismissal: string;
    cooldownAfterDismissalMeasure: TimeMeasure;
    cooldownAfterSubmission: string;
    cooldownAfterSubmissionMeasure: TimeMeasure;
}

const validationSchema = Yup.object().shape({
    cooldownAfterDismissal: Yup.number()
        .required('This field is required.')
        .min(0, 'This field must be a positive number.'),
    cooldownAfterSubmission: Yup.number()
        .required('This field is required.')
        .min(0, 'This field must be a positive number.')
});

export const cooldownMeasureOptions = [
    { label: 'Week(s)', value: TimeMeasure.WEEK },
    { label: 'Day(s)', value: TimeMeasure.DAY },
    { label: 'Hour(s)', value: TimeMeasure.HOUR },
    { label: 'Minute(s)', value: TimeMeasure.MINUTE }
];

export const minimumRatingOptions = [
    {
        value: '5',
        label: (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Rating value={5} readOnly />
                <Typography variant="body2">5 stars only (most selective)</Typography>
            </Box>
        )
    },
    {
        value: '4',
        label: (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Rating value={4} readOnly />
                <Typography variant="body2">4 stars or higher</Typography>
            </Box>
        )
    },
    {
        value: '3',
        label: (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Rating value={3} readOnly />
                <Typography variant="body2">3 stars or higher</Typography>
            </Box>
        )
    },
    {
        value: '2',
        label: (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Rating value={2} readOnly />
                <Typography variant="body2">2 stars or higher</Typography>
            </Box>
        )
    },
    {
        value: '1',
        label: (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Rating value={1} readOnly />
                <Typography variant="body2">1 star or higher</Typography>
            </Box>
        )
    }
];

export const FormDirtyObserver = ({
    onChange,
    onValidityChange
}: {
    onChange: (dirty: boolean) => void;
    onValidityChange?: (isValid: boolean) => void;
}): null => {
    const { dirty, isValid } = useFormikContext();
    React.useEffect(() => {
        onChange(dirty);
    }, [dirty, onChange]);
    React.useEffect(() => {
        onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);
    return null;
};

interface ReviewsGlobalSettingsProps {
    globalSettings: IPublicAndPrivateSettings;
    isLoading: boolean;
    isSaving: boolean;
    onSave: (values: Partial<IPublicAndPrivateSettings>) => Promise<void>;
    onDirtyChange?: (dirty: boolean) => void;
    onValidityChange?: (isValid: boolean) => void;
    submitRef?: React.MutableRefObject<(() => void) | null>;
    hasReviewsWritePermission: boolean;
}
export const ReviewsGlobalSettings = ({
    globalSettings,
    isLoading,
    isSaving,
    onSave,
    onDirtyChange,
    onValidityChange,
    submitRef,
    hasReviewsWritePermission
}: ReviewsGlobalSettingsProps) => {
    const theme = useTheme();
    const [isFormikDirty, setIsFormikDirty] = React.useState(false);

    const initialValues = React.useMemo<ReviewsGlobalSettingsFormData>(() => {
        const cooldownAfterDismissal = getMinutesToExpiryComponents(
            globalSettings.prompts?.cooldownMins?.onDismissed ?? 0
        );
        const cooldownAfterSubmission = getMinutesToExpiryComponents(
            globalSettings.prompts?.cooldownMins?.onCompleted ?? 0
        );
        return {
            cooldownAfterDismissal: cooldownAfterDismissal.value.toString(),
            cooldownAfterDismissalMeasure: cooldownAfterDismissal.measure,
            cooldownAfterSubmission: cooldownAfterSubmission.value.toString(),
            cooldownAfterSubmissionMeasure: cooldownAfterSubmission.measure,
            reviewsEnabledByMerchant: globalSettings.reviewsEnabledByMerchant ?? false,
            minimumRating: globalSettings.reviews?.minimumRatingToTriggerExternalReview?.toString() ?? '5'
        };
    }, [globalSettings]);

    const handleSave = React.useCallback(
        async (
            values: ReviewsGlobalSettingsFormData,
            { resetForm }: FormikHelpers<ReviewsGlobalSettingsFormData>
        ) => {
            const cooldownAfterDismissalMins = getTimeUnitsToMinutes(
                parseIntOrNull(values.cooldownAfterDismissal),
                values.cooldownAfterDismissalMeasure
            );
            const cooldownAfterSubmissionMins = getTimeUnitsToMinutes(
                parseIntOrNull(values.cooldownAfterSubmission),
                values.cooldownAfterSubmissionMeasure
            );
            const settingsToUpdate: IPublicAndPrivateSettings = {
                reviewsEnabledByMerchant: values.reviewsEnabledByMerchant,
                reviews: {
                    minimumRatingToTriggerExternalReview: parseIntOrNull(values.minimumRating)
                },
                prompts: {
                    cooldownMins: {
                        onDismissed: cooldownAfterDismissalMins,
                        onCompleted: cooldownAfterSubmissionMins
                    }
                }
            };
            await onSave(settingsToUpdate);
            resetForm({ values });
        },
        [onSave]
    );

    React.useEffect(() => {
        onDirtyChange?.(isFormikDirty);
    }, [isFormikDirty, onDirtyChange]);

    if (isLoading) {
        return (
            <Box
                px={2}
                py={1}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <MuiForm<ReviewsGlobalSettingsFormData>
            onSubmit={handleSave}
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {({ submitForm, isValid, dirty }) => {
                if (submitRef) {
                    submitRef.current = submitForm;
                }
                return (
                    <Form>
                        <FormDirtyObserver onChange={setIsFormikDirty} onValidityChange={onValidityChange} />
                        <Grid container spacing={2} sx={{ minHeight: '100%', position: 'relative' }}>
                            <Grid
                                size={{
                                    xs: 12
                                }}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    gap={1}
                                    alignItems="center"
                                    sx={{
                                        backgroundColor: theme.palette.grey[100],
                                        padding: 1,
                                        borderRadius: 1
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: '1.25rem' }} />
                                    <Typography variant="body2">
                                        These settings apply to <b>all locations</b> by default. To override
                                        settings for a specific location, use the <b>Locations</b> tab.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ border: 1, borderRadius: 1, padding: 2, borderColor: 'divider' }}
                                >
                                    <Box>
                                        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                            <Typography>Collect Ratings</Typography>
                                            <Tooltip
                                                title="Enable rating collection for this location. Once active, Pepper will prompt
                                        customers to leave a rating after any completed order — whether it's a mobile or web order,
                                        or a scan-to-order at the POS.
                                        Prompts are intelligently delayed based on the ordering scenario, so customers are always
                                        asked at the right moment."
                                            >
                                                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                            </Tooltip>
                                        </Box>
                                        <Typography variant="caption">
                                            Toggle rating collection on or off for all locations
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <SwitchFormField
                                            name="reviewsEnabledByMerchant"
                                            disabled={!hasReviewsWritePermission}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}
                            >
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={2}
                                    sx={{ border: 1, borderRadius: 1, padding: 2, borderColor: 'divider' }}
                                >
                                    <Box>
                                        <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                                            <Typography variant="body1" fontWeight="medium">
                                                Minimum Rating for Google Redirect
                                            </Typography>
                                            <Tooltip
                                                title="Set the minimum star rating required to redirect customers to Google for all locations.
                             Individual locations can override this in the Locations tab."
                                            >
                                                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                            </Tooltip>
                                        </Box>
                                        <Typography variant="caption">
                                            Only redirect customers who rate this highly or above
                                        </Typography>
                                    </Box>
                                    <SelectFormField
                                        name="minimumRating"
                                        options={minimumRatingOptions}
                                        variant="outlined"
                                        disabled={!hasReviewsWritePermission}
                                    />
                                    <Typography variant="caption">
                                        Users will not be redirected to Google unless a valid Google Review
                                        URL is entered for their location in the Locations tab.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}
                                display="flex"
                                flexDirection="column"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={3}
                                    sx={{ border: 1, borderRadius: 1, padding: 2, borderColor: 'divider' }}
                                >
                                    <Box>
                                        <Typography>Cooldown Settings</Typography>
                                        <Typography variant="caption">
                                            These settings apply to all locations
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" fontWeight="medium">
                                                Cooldown After Dismissal
                                            </Typography>
                                            <Tooltip
                                                title="If a customer dismisses the prompt without submitting a review,
                                            how long should we wait before asking about that location again?"
                                            >
                                                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                            </Tooltip>
                                        </Box>
                                        <Grid container spacing={1}>
                                            <Grid
                                                size={{
                                                    xs: 8
                                                }}
                                            >
                                                <NumberFormField
                                                    name="cooldownAfterDismissal"
                                                    variant="outlined"
                                                    disabled={!hasReviewsWritePermission}
                                                />
                                            </Grid>
                                            <Grid
                                                size={{
                                                    xs: 4
                                                }}
                                            >
                                                <SelectFormField
                                                    name="cooldownAfterDismissalMeasure"
                                                    options={cooldownMeasureOptions}
                                                    variant="outlined"
                                                    disabled={!hasReviewsWritePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" fontWeight="medium">
                                                Cooldown After Submission
                                            </Typography>
                                            <Tooltip
                                                title="After a customer submits a review,
                                    how long should we wait before asking for another review for that location?"
                                            >
                                                <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                            </Tooltip>
                                        </Box>
                                        <Grid container spacing={1}>
                                            <Grid
                                                size={{
                                                    xs: 8
                                                }}
                                            >
                                                <NumberFormField
                                                    name="cooldownAfterSubmission"
                                                    variant="outlined"
                                                    disabled={!hasReviewsWritePermission}
                                                />
                                            </Grid>
                                            <Grid
                                                size={{
                                                    xs: 4
                                                }}
                                            >
                                                <SelectFormField
                                                    name="cooldownAfterSubmissionMeasure"
                                                    options={cooldownMeasureOptions}
                                                    variant="outlined"
                                                    disabled={!hasReviewsWritePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}
                                position="sticky"
                                bottom={0}
                            >
                                <StyledButtonWrapper>
                                    <StyledLoadingButton
                                        loading={isSaving}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={submitForm}
                                        disabled={
                                            !isValid || isSaving || !dirty || !hasReviewsWritePermission
                                        }
                                    >
                                        Save
                                    </StyledLoadingButton>
                                </StyledButtonWrapper>
                            </Grid>
                        </Grid>
                    </Form>
                );
            }}
        </MuiForm>
    );
};
